/** WGS84-only GeoJSON validation (CRS + bounds). Matches server FR-021 rules. */

export const WGS84_GEOJSON_ERROR =
  'GeoJSON must use WGS84 (EPSG:4326) coordinates only.';

const ALLOWED_GEOMETRY_TYPES = new Set([
  'Point',
  'LineString',
  'Polygon',
  'MultiPoint',
  'MultiLineString',
  'MultiPolygon',
]);

type GeoJsonCrs = {
  type?: string;
  properties?: { name?: string };
  name?: string;
};

function extractCrsName(crs: unknown): string | null {
  if (typeof crs !== 'object' || crs === null) return null;
  const record = crs as GeoJsonCrs;
  const fromProps = record.properties?.name;
  if (typeof fromProps === 'string' && fromProps.trim()) return fromProps.trim();
  if (typeof record.name === 'string' && record.name.trim()) return record.name.trim();
  return null;
}

function normalizeCrsIdentifier(name: string): string {
  const upper = name.trim().toUpperCase();
  const urnMatch = upper.match(/EPSG::(\d+)/);
  if (urnMatch) return `EPSG:${urnMatch[1]}`;
  const epsgMatch = upper.match(/EPSG:(\d+)/);
  if (epsgMatch) return `EPSG:${epsgMatch[1]}`;
  if (upper.includes('CRS84')) return 'OGC:CRS84';
  return upper;
}

export function isWgs84Crs(crs: unknown): boolean {
  if (crs == null) return true;
  const name = extractCrsName(crs);
  if (!name) return false;
  const normalized = normalizeCrsIdentifier(name);
  if (normalized === 'EPSG:4326' || normalized === 'OGC:CRS84') return true;
  if (/EPSG[^0-9]*4326/i.test(name)) return true;
  if (/CRS84/i.test(name) && !/28992/i.test(name)) return true;
  return false;
}

function isPosition(value: unknown): value is [number, number, ...number[]] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number' &&
    !Number.isNaN(value[0]) &&
    !Number.isNaN(value[1]) &&
    !Array.isArray(value[0])
  );
}

function positionInBounds(lon: number, lat: number): boolean {
  return lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;
}

function walkCoordinates(coords: unknown, visit: (lon: number, lat: number) => void): void {
  if (isPosition(coords)) {
    visit(coords[0], coords[1]);
    return;
  }
  if (Array.isArray(coords)) {
    for (const child of coords) {
      walkCoordinates(child, visit);
    }
  }
}

function geometryCoordinatesInWgs84Bounds(geometry: unknown): boolean {
  if (typeof geometry !== 'object' || geometry === null) return true;
  const geom = geometry as { type?: string; coordinates?: unknown };
  if (!geom.type || !ALLOWED_GEOMETRY_TYPES.has(geom.type)) return true;
  let inBounds = true;
  walkCoordinates(geom.coordinates, (lon, lat) => {
    if (!positionInBounds(lon, lat)) inBounds = false;
  });
  return inBounds;
}

/** Partial FeatureCollection slice for incremental stream validation. */
export type Wgs84GeoJsonDocumentSlice = {
  /** Set when root `crs` has been parsed from the stream (may appear after features). */
  crs?: unknown;
  /** Batch of features to validate coordinates for (allowed geometries only). */
  features?: unknown[];
};

/**
 * Validate WGS84 CRS and/or coordinate bounds for a document slice.
 * Pass `crs` only when the stream has parsed it; pass feature batches as they arrive.
 */
export function validateWgs84GeoJsonDocument(slice: Wgs84GeoJsonDocumentSlice): string | null {
  if ('crs' in slice && !isWgs84Crs(slice.crs)) {
    return WGS84_GEOJSON_ERROR;
  }

  const features = Array.isArray(slice.features) ? slice.features : [];
  for (const feature of features) {
    if (typeof feature !== 'object' || feature === null) continue;
    const geometry = (feature as { geometry?: unknown }).geometry;
    if (!geometryCoordinatesInWgs84Bounds(geometry)) {
      return WGS84_GEOJSON_ERROR;
    }
  }

  return null;
}
